/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
'use strict';
import { Injectable, NgZone } from '@angular/core';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import { Collection, MapBrowserEvent } from 'ol';
import { Color } from 'ol/color';
import { ColorLike } from 'ol/colorlike';
import { Coordinate } from 'ol/coordinate';
import { Extent, getSize } from 'ol/extent';
import Feature from 'ol/Feature';
import { Point, Polygon } from 'ol/geom';
import { defaults, DragBox, DragPan, Draw, Interaction, Pointer } from 'ol/interaction';
import { DrawEvent } from 'ol/interaction/Draw';
import { Pixel } from 'ol/pixel';
import { toLonLat } from 'ol/proj';
import { Vector as VectorSource } from 'ol/source';
import { Circle, Fill, Stroke, Style, Text } from 'ol/style';
import { AppConfigState } from '@core/state/appConfig/app-config.state';

import { MapQuerySpatialFilter } from '@app/types/Map';
import { MeasureOptions } from '@app/types/measure.types';
import { DeviceService } from '@shared/services/device.service';
import { LoggerService } from '@shared/services/logger.service';
import { MeasureService } from '@shared/services/measure.service';
import { DigitizableLayer } from '../command/digitize-point/state/digitize-point-config.model';
import { Measure } from '../interaction/measure.interaction';
import { CopyCoordinates, MapCreated, UserSelection } from '../state/active-map.actions';
import { MapService } from './map.service';
import { QueryService } from './query.service';

@Injectable()
export class MapInteractionsService {
    private _interactionStore: Map<string, Interaction>;
    private markupUndoStack: Feature[];
    private markupColor: Color;
    private markupText: string;
    private markupDrawEndListener: (Event) => any;
    private polygonSelectColor: ColorLike;

    /** @member clickTolerance {number} The click tolerance to set in the Draw Interaction */
    private readonly clickTolerance: number;

    /**
     *
     * @param {LoggerService} logger
     * @param {MapService} mapService
     * @param {QueryService} queryService
     * @param {MeasureService} measureService
     * @param {DeviceService} deviceService
     * @param {NgZone} ngZone
     * @param {Actions} actions$
     * @param {Store} store
     */
    constructor(
        private logger: LoggerService,
        private mapService: MapService,
        private queryService: QueryService,
        private measureService: MeasureService,
        private deviceService: DeviceService,
        private ngZone: NgZone,
        private actions$: Actions,
        private store: Store
    ) {
        this._interactionStore = new Map();
        this.markupUndoStack = [];

        // on map reload, clear all interaction and reset to default
        this.actions$.pipe(ofActionSuccessful(MapCreated)).subscribe(() => {
            this.removeInteractions();
            this._interactionStore.clear();
            this.enablePanInteraction();
        });

        // set clickTolerance based on touch capability
        this.clickTolerance = this.deviceService.hasTouchEvents ? 6 : 2;
    }

    public pauseInteractions() {
        this.mapService.map.getInteractions().forEach((i: Interaction) => {
            i.setActive(false);
        });
    }

    public resumeInteractions() {
        this.mapService.map.getInteractions().forEach((i: Interaction) => {
            i.setActive(true);
        });
    }

    public pauseSelectInteractions() {
        this.mapService.map.getInteractions().forEach((i: Interaction) => {
            if (['select-point', 'select-box', 'select-polygon'].includes(i.get('name'))) {
                i.setActive(false);
            }
        });
    }

    /**
     * Return an ol.Collection of the default interactions
     */
    public getDefaultInteractions(): Collection<Interaction> {
        return this.ngZone.runOutsideAngular(() =>
            defaults({
                altShiftDragRotate: false,
                dragPan: false,
                pinchRotate: false,
                zoomDuration: 0,
            }).extend([this.getDragPanInteraction(), this.getSelectPointInteraction()])
        );
    }

    public enablePanInteraction() {
        this.enableDefaultInteractions();
        this.mapService.setCursor('move');
    }

    public enableSelectBoxInteraction() {
        const interactionName = 'select-box';
        // minimum select box size
        const pixelBuffer = 2;
        // remove any current interactions
        this.removeInteractions();
        let selectInteraction;
        if (this._interactionStore.has(interactionName)) {
            selectInteraction = this._interactionStore.get(interactionName);
        } else {
            selectInteraction = new DragBox({
                // allow select on click
                boxEndCondition: () => true,
            });
            selectInteraction.set('name', interactionName);
            selectInteraction.on('boxend', (e) => {
                let boundingBox = selectInteraction.getGeometry().getExtent();
                const modifiers = MapInteractionsService.getModifierKeys(e);
                const size = getSize(boundingBox);
                if (0 === size[0] || 0 === size[1]) {
                    const ul = this.mapService.map.getPixelFromCoordinate([boundingBox[0], boundingBox[1]]);
                    const br = this.mapService.map.getPixelFromCoordinate([boundingBox[2], boundingBox[3]]);
                    ul[0] -= pixelBuffer;
                    ul[1] -= pixelBuffer;
                    br[0] += pixelBuffer;
                    br[1] += pixelBuffer;
                    const coordinatesUL = this.mapService.map.getCoordinateFromPixel(ul);
                    const coordinatesBR = this.mapService.map.getCoordinateFromPixel(br);
                    boundingBox = [coordinatesBR[0], coordinatesBR[1], coordinatesUL[0], coordinatesUL[1]];
                }

                const filter: MapQuerySpatialFilter = {
                    filter: { type: 'bbox', bbox: boundingBox },
                    type: 'spatial',
                };
                this.store.dispatch(new UserSelection());
                this.queryService.doQuery(filter, modifiers.shiftKey).subscribe();
            });
            this._interactionStore.set(interactionName, selectInteraction);
        }
        const map = this.mapService.map;
        map.addInteraction(selectInteraction);
        this.mapService.setCursor('default');
    }

    public enableZoomInInteraction() {
        if (this.deviceService.hasTouchEvents) {
            this.enablePanInteraction();
        } else {
            this.enableMouseZoomInInteraction();
        }
    }

    public enableZoomOutInteraction() {
        if (this.deviceService.hasTouchEvents) {
            this.enablePanInteraction();
        } else {
            this.enableMouseZoomOutInteraction();
        }
    }

    public enableMouseZoomInInteraction() {
        const interactionName = 'mouse-zoom-in';
        const map = this.mapService.map;

        // remove any current interactions
        this.removeInteractions();
        let zoomInInteraction;
        if (this._interactionStore.has(interactionName)) {
            zoomInInteraction = this._interactionStore.get(interactionName);
        } else {
            zoomInInteraction = new DragBox({
                // allow select on click
                boxEndCondition: () => true,
            });
            zoomInInteraction.set('name', interactionName);
            zoomInInteraction.on('boxend', () => {
                const mapObj = this.mapService.map;
                const boundingBox = zoomInInteraction.getGeometry().getExtent();
                mapObj.getView().fit(boundingBox, { size: mapObj.getSize() });
            });
            this._interactionStore.set(interactionName, zoomInInteraction);
        }
        map.addInteraction(zoomInInteraction);
        this.mapService.setCursor('zoom-in');
    }

    public enableMouseZoomOutInteraction() {
        const interactionName = 'mouse-zoom-out';
        // remove any current interactions
        this.removeInteractions();
        let zoomOutInteraction;
        if (this._interactionStore.has(interactionName)) {
            zoomOutInteraction = this._interactionStore.get(interactionName);
        } else {
            zoomOutInteraction = new Pointer({
                handleEvent: (event) => {
                    if ('click' === event.type) {
                        const view = event.map.getView();
                        view.setCenter(event.coordinate);
                        const zoom = view.getZoom();
                        view.setZoom(zoom - 1);
                    }
                    return true;
                },
            });
            zoomOutInteraction.set('name', interactionName);
            this._interactionStore.set(interactionName, zoomOutInteraction);
        }
        const map = this.mapService.map;
        map.addInteraction(zoomOutInteraction);
        this.mapService.setCursor('zoom-out');
    }

    /**
     * Turns on digitizing for a single point
     * @param fn A function to post-process the point, expects to be bound to
     * context/this
     * @param layer {string}
     * @param key {any}
     */
    public enableDigitizePointInteraction(fn, layer, key?) {
        // TODO: Extend from Draw interaction, to allow accepting fn as a parameter after creation

        const interactionName = 'digitize-point';
        // remove any current interactions
        this.removeInteractions();
        this.pauseSelectInteractions();
        const digitizeInteraction = new Draw({
            clickTolerance: 4,
            maxPoints: 1,
            style: new Style({
                image: new Circle({
                    fill: new Fill({ color: 'rgba(0, 0, 0, 0)' }),
                    radius: 0,
                    stroke: new Stroke({
                        color: 'rgba(0, 0, 0, 0)',
                        width: 0,
                    }),
                }),
            }),
            type: 'Point',
        });
        digitizeInteraction.set('name', interactionName);
        const drawEndFn = (digitizeLayer: DigitizableLayer, digitizeKey: any, drawEvent: DrawEvent) => {
            const coordinates = (drawEvent.feature.getGeometry() as Point).getCoordinates();
            if (digitizeKey) {
                fn(digitizeLayer, digitizeKey, coordinates);
            } else {
                fn(digitizeLayer, null, coordinates);
            }
            setTimeout(() => {
                this.resumeInteractions();
                this.enablePanInteraction();
            }, 300);
        };
        digitizeInteraction.once('drawend', drawEndFn.bind(this, layer, key));
        const map = this.mapService.map;
        map.addInteraction(digitizeInteraction);
        this.mapService.setCursor('crosshair');
    }

    public enableMarkup(markupType, color, text) {
        this.markupColor = color;
        if (text) {
            this.markupText = text;
        }
        const interactionName = `markup-${markupType}`;
        let markupInteraction = null;
        this.removeInteractions();
        const markupLayer = this.mapService.getVectorLayer('markup');
        let markupStyle;

        if (this._interactionStore.has(interactionName)) {
            markupInteraction = this._interactionStore.get(interactionName);
            markupInteraction.un('drawend', this.markupDrawEndListener);
        } else {
            switch (markupType) {
                case 'line':
                    markupStyle = () =>
                        new Style({
                            fill: null,
                            stroke: new Stroke({
                                color: this.markupColor,
                                width: 2,
                            }),
                        });
                    markupInteraction = new Draw({
                        clickTolerance: 4,
                        source: markupLayer.getSource() as VectorSource,
                        style: markupStyle,
                        type: 'LineString',
                    });
                    break;
                case 'rect':
                    markupStyle = () =>
                        new Style({
                            fill: null,
                            stroke: new Stroke({
                                color: this.markupColor,
                                width: 2,
                            }),
                        });
                    markupInteraction = new Draw({
                        geometryFunction: (coordinates: Coordinate[], geometry) => {
                            const start = coordinates[0];
                            const end = coordinates[1];
                            const polyCoords = [[start, [start[0], end[1]], end, [end[0], start[1]], start]];
                            if (!geometry) {
                                geometry = new Polygon(polyCoords);
                            } else {
                                (geometry as Polygon).setCoordinates([
                                    [start, [start[0], end[1]], end, [end[0], start[1]], start],
                                ]);
                            }
                            return geometry;
                        },
                        maxPoints: 2,
                        source: markupLayer.getSource() as VectorSource,
                        style: markupStyle,
                        type: 'LineString',
                    });
                    break;
                case 'circle':
                    markupStyle = () =>
                        new Style({
                            fill: null,
                            stroke: new Stroke({
                                color: this.markupColor,
                                width: 2,
                            }),
                        });
                    markupInteraction = new Draw({
                        source: markupLayer.getSource() as VectorSource,
                        style: markupStyle,
                        type: 'Circle',
                    });
                    break;
                case 'polygon':
                    markupStyle = () =>
                        new Style({
                            fill: null,
                            stroke: new Stroke({
                                color: this.markupColor,
                                width: 2,
                            }),
                        });
                    markupInteraction = new Draw({
                        source: markupLayer.getSource() as VectorSource,
                        style: markupStyle,
                        type: 'Polygon',
                    });
                    break;
                case 'text':
                    markupStyle = () =>
                        new Style({
                            text: new Text({
                                fill: new Fill({ color: this.markupColor }),
                                font: 'condensed 14pt Roboto',
                                text: this.markupText,
                            }),
                        });
                    markupInteraction = new Draw({
                        source: markupLayer.getSource() as VectorSource,
                        style: new Style({
                            fill: null,
                            stroke: null,
                        }),
                        type: 'Point',
                    });
                    break;
                default:
                    throw new Error(`Unknown markup type [${markupType}] provided to \`enableMarkup\`.`);
            }
            this.markupDrawEndListener = this.getMarkupDrawEndListener(markupStyle);
            markupInteraction.on('drawend', this.markupDrawEndListener);
            markupInteraction.set('name', interactionName);
            this._interactionStore.set(interactionName, markupInteraction);
        }
        const map = this.mapService.map;
        map.addInteraction(markupInteraction);
        this.mapService.setCursor('crosshair');
    }

    public undoMarkup() {
        const feature = this.markupUndoStack.pop();
        this.removeMarkupFeature(feature);
    }

    public clearMarkup() {
        const layer = this.mapService.getVectorLayer('markup');
        if (layer) {
            layer.getSource().clear(true);
        }
    }

    public removeMarkupFeature(feature) {
        const layer = this.mapService.getVectorLayer('markup');
        if (layer) {
            layer.getSource().removeFeature(feature);
        }
    }

    public enableMeasure(options: any) {
        const interactionName = 'measure-line';
        let measureInteraction: any;
        this.removeInteractions();
        this.measureService.setMap(this.mapService.map);

        if (this._interactionStore.has(interactionName)) {
            measureInteraction = this._interactionStore.get(interactionName) as Measure;
        } else {
            const measureOptions: MeasureOptions = {
                clickTolerance: this.clickTolerance,
                store: this.store,
            };
            if (options['displayUnits']) {
                measureOptions.displayUnits = options['displayUnits'];
            }
            measureInteraction = new Measure(measureOptions);
            measureInteraction.on('measurestart', options.onStart ?? function () {});
            measureInteraction.on('measurechange', options.onChange ?? function () {});
            measureInteraction.on('measureend', options.onEnd ?? function () {});
            measureInteraction.set('name', interactionName);
        }
        this._interactionStore.set(interactionName, measureInteraction);

        this.mapService.map.addInteraction(measureInteraction);
        this.mapService.setCursor('crosshair');
    }

    public clearMeasure(): void {
        if (this._interactionStore.has('measure-line')) {
            const measureInteraction = this._interactionStore.get('measure-line') as Measure;
            measureInteraction.clearMeasure();
        }
    }

    public enablePolygonSelect(selectFn) {
        const interactionName = 'select-polygon';
        this.polygonSelectColor = '#838383';
        let polygonSelectInteraction;
        this.removeInteractions();
        const polygonSelectLayer = this.mapService.createPolygonSelectLayer();
        const polygonSelectStyle = new Style({
            fill: null,
            stroke: new Stroke({
                color: this.polygonSelectColor,
                width: 1,
            }),
        });

        if (this._interactionStore.has(interactionName)) {
            polygonSelectInteraction = this._interactionStore.get(interactionName);
        } else {
            polygonSelectInteraction = new Draw({
                clickTolerance: 4,
                source: polygonSelectLayer.getSource(),
                style: polygonSelectStyle,
                type: 'Polygon',
            });
            polygonSelectInteraction.set('name', interactionName);
        }
        polygonSelectInteraction.once('drawend', selectFn, this);
        this._interactionStore.set(interactionName, polygonSelectInteraction);
        this.mapService.map.addInteraction(polygonSelectInteraction);
        this.mapService.setCursor('crosshair');
    }

    public clearPolygonSelect() {
        const layer = this.mapService.getPolygonSelectLayer();
        if (layer) {
            layer.getSource().clear(true);
        }
        const polygonSelectInteraction = this._interactionStore.get('select-polygon');
        this.mapService.map.removeInteraction(polygonSelectInteraction);
    }

    public enableRedline(redlineType, interactionOptions, drawEndFn) {
        const interactionName = 'redline';
        if (this._interactionStore.has(interactionName)) {
            this._interactionStore.delete(interactionName);
        }
        this.removeInteractions();
        let redlineInteraction = null;
        switch (redlineType) {
            case 'line':
            case 'point':
                redlineInteraction = new Draw(interactionOptions);
                redlineInteraction.on('drawend', drawEndFn);
                break;
            default:
                break;
        }
        redlineInteraction.set('name', interactionName);
        this._interactionStore.set(interactionName, redlineInteraction);
        this.mapService.map.addInteraction(redlineInteraction);
        this.mapService.setCursor('crosshair');
    }

    public enableClickToCopyCoordinates() {
        const interactionName = 'copy-coordinates';
        let copyInteraction;
        this.removeInteractions();
        if (this._interactionStore.has(interactionName)) {
            copyInteraction = this._interactionStore.get(interactionName);
        } else {
            const handler = (e: MapBrowserEvent<MouseEvent>) => {
                if ('click' === e.type) {
                    const userCoords = this.mapService.map.getCoordinateFromPixel(e.pixel);
                    const globalCoords = toLonLat(userCoords);
                    this.store.dispatch(new CopyCoordinates(globalCoords));
                    this.enablePanInteraction();
                    return false;
                }
                return true;
            };
            copyInteraction = new Pointer({
                // create pixel buffer around touch point
                handleDownEvent: () => false,
                handleEvent: handler,
                handleUpEvent: () => false,
            });
            copyInteraction.set('name', interactionName);
        }
        this._interactionStore.set(interactionName, copyInteraction);
        this.mapService.map.addInteraction(copyInteraction);
        this.mapService.setCursor('crosshair');
    }

    /**
     * Removes all interactions added by the mapInteractions service. Does not affect
     * default interactions (unless overridden by the service.)
     */
    public removeInteractions() {
        this._interactionStore.forEach((interaction) => {
            this.mapService.map.removeInteraction(interaction);
        });
        this.mapService.setCursor('move');
    }

    private static getModifierKeys(e) {
        const event = e.mapBrowserEvent ? e.mapBrowserEvent : e;
        return {
            altKey: event.originalEvent.altKey,
            ctrlKey: event.originalEvent.ctrlKey,
            shiftKey: event.originalEvent.shiftKey,
        };
    }

    private enableDefaultInteractions() {
        this.mapService.map.getInteractions().clear();
        this.getDefaultInteractions().forEach((item) => {
            this.mapService.map.addInteraction(item);
        });
    }

    private getDragPanInteraction(): DragPan {
        const interactionName = 'drag-pan';
        let dragPan: DragPan;
        if (this._interactionStore.has(interactionName)) {
            dragPan = this._interactionStore.get(interactionName) as DragPan;
        } else {
            dragPan = new DragPan({ kinetic: null });
            dragPan.set('name', interactionName);
            this._interactionStore.set(interactionName, dragPan);
        }

        return dragPan;
    }
    /**
     * Returns an interaction that can select a point by a click (using the selectTolerance setting)
     * or by dragging a window.
     * @private
     * @return
     */
    private getSelectPointInteraction(): Interaction {
        const interactionName = 'select-point';
        let selectInteraction: Interaction;
        if (this._interactionStore.has(interactionName)) {
            selectInteraction = this._interactionStore.get(interactionName);
        } else {
            const pixelBuffer = this.selectTolerance;
            const handler = (e: MapBrowserEvent<MouseEvent>) => {
                if ('click' === e.type) {
                    const modifiers = MapInteractionsService.getModifierKeys(e);
                    const ul: Pixel = [e.pixel[0] - pixelBuffer, e.pixel[1] - pixelBuffer];
                    const br: Pixel = [e.pixel[0] + pixelBuffer, e.pixel[1] + pixelBuffer];
                    const coordinatesUL = this.mapService.map.getCoordinateFromPixel(ul);
                    const coordinatesBR = this.mapService.map.getCoordinateFromPixel(br);
                    const boundingBox: Extent = [
                        Math.min(coordinatesBR[0], coordinatesUL[0]),
                        Math.min(coordinatesBR[1], coordinatesUL[1]),
                        Math.max(coordinatesBR[0], coordinatesUL[0]),
                        Math.max(coordinatesBR[1], coordinatesUL[1]),
                    ];

                    this.store.dispatch(new UserSelection());
                    const filter: MapQuerySpatialFilter = {
                        filter: { type: 'bbox', bbox: boundingBox },
                        type: 'spatial',
                    };
                    this.queryService.doQuery(filter, modifiers.shiftKey).subscribe();
                    return false;
                }
                return true;
            };
            selectInteraction = new Pointer({
                // create pixel buffer around touch point
                handleDownEvent: () => false,
                handleEvent: handler,
                handleUpEvent: () => false,
            });
            selectInteraction.set('name', interactionName);
            this._interactionStore.set(interactionName, selectInteraction);
        }

        return selectInteraction;
    }

    private getMarkupDrawEndListener(markupStyleBuilder) {
        return (drawEvent) => {
            this.markupUndoStack.push(drawEvent.feature);
            drawEvent.feature.setStyle(markupStyleBuilder());
        };
    }

    get hasMarkupFeatures() {
        return 0 < this.markupUndoStack.length;
    }

    get selectTolerance() {
        return this.store.selectSnapshot(AppConfigState.selectTolerance);
    }
}
